/**
 * The user session.
 *
 * Nothing inside the app runs without a real account. Firebase keeps the
 * session in IndexedDB, so a refresh returns the same uid and the same
 * progress; a different device signs in to the same account and finds the work
 * waiting there, because the account is an e-mail address rather than a
 * browser.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth, firebaseConfigured } from "@/lib/firebase";
import { currentLocale } from "@/lib/router";
import { pendingMarketingConsent, setPendingMarketingConsent } from "@/lib/marketing-consent";
import { forgetSignupSource, pendingSignupSource } from "@/lib/signup-source";
import { currentEntry, takeFreshEntry } from "@/lib/entry";
import { api } from "@/lib/api";
import { setSignedIn } from "@/lib/analytics";

export type SessionStatus =
  /** Waiting to hear from Firebase whether anyone is signed in. */
  | "loading"
  /** Nobody is signed in. Everything but the landing page asks them to. */
  | "signedOut"
  /** A user exists and the server knows about them. */
  | "ready"
  | "error";

interface Session {
  status: SessionStatus;
  uid: string | null;
  email: string | null;
  /** The name Google gave us, when sign-in was Google. */
  name: string | null;
  /** Whether the Worker lets this account into `/admin`. Shows the menu entry; decides nothing. */
  admin: boolean;
  /** The first-visit questions are still to be asked (`shared/onboarding.ts`). */
  onboard: boolean;
  /** They have been answered or skipped. */
  onboarded: () => void;
  error: string | null;
  /** Re-reads the profile after the account changes. */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession used outside SessionProvider");
  return value;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>(
    firebaseConfigured ? "loading" : "ready",
  );
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [admin, setAdmin] = useState(false);
  const [onboard, setOnboard] = useState(false);
  const onboarded = useCallback(() => setOnboard(false), []);

  const register = useCallback(async (next: User | null) => {
    setUser(next);
    if (!next) {
      setAdmin(false);
      setStatus("signedOut");
      setError(null);
      return;
    }
    try {
      const consent = pendingMarketingConsent();
      const source = pendingSignupSource();
      const profile = await api.session(currentLocale(), consent, source, currentEntry(), takeFreshEntry());
      // Delivered; a later sign-in on this device must not give it a second time.
      if (consent) setPendingMarketingConsent(false);
      // Written if the account is new, ignored if not — either way it is spent.
      if (source) forgetSignupSource();
      setAdmin(profile.admin === true);
      setOnboard(profile.onboard === true);
      setStatus("ready");
      setError(null);
    } catch (err) {
      setError((err as Error).message);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    // Without Firebase the Worker recognises a fixed development user, so
    // there is nothing to observe.
    if (!firebaseConfigured || !auth) {
      void register({ uid: "local-dev", email: null, displayName: null } as User);
      return;
    }
    return onAuthStateChanged(
      auth,
      (next) => void register(next),
      (err) => {
        setError(err.message);
        setStatus("error");
      },
    );
  }, [register]);

  const refresh = useCallback(async () => {
    await register(auth?.currentUser ?? null);
  }, [register]);

  // Set during render, not in an effect: the page view for the page this
  // status reveals is reported from an effect, and has to see it already.
  setSignedIn(status === "ready");

  const value = useMemo<Session>(
    () => ({
      status,
      uid: user?.uid ?? null,
      email: user?.email ?? null,
      name: user?.displayName ?? null,
      admin,
      onboard,
      onboarded,
      error,
      refresh,
    }),
    [status, user, admin, onboard, onboarded, error, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
